/**
 * `contacts.html`'s Relationship Map — an account's buying committee as an
 * org chart, plus a coverage read of which roles are and aren't represented.
 *
 * The prototype draws this from fixture data with fabricated edges. The real
 * shape is simpler and already sitting in the schema: `crm.contact.reports_to_id`
 * is a real self-FK (§5), so the tree here is contacts at the chosen account,
 * nested by who reports to whom — not an invented graph. "Linked records"
 * lists the same contacts `crm.contact_relationship` names instead (fetched
 * per contact via `GET /contacts/{id}/relationships`), which is the actual
 * edge table for "influences" / "ally of" style connections that aren't a
 * reporting line.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Modal } from '../../components/common/index.jsx';
import { Spinner } from '../../components/common/Loading.jsx';
import { queryKeys } from '../../app/queryClient.js';
import * as contactsApi from '../../api/contacts.api.js';
import { useContacts, useContactOptions } from './useContacts.js';

const ROLE_COLOR = {
  'Economic buyer': 'var(--rose)',
  Champion: 'var(--emerald)',
  'Technical DM': 'var(--violet)',
  Blocker: 'var(--amber)',
  Buyer: 'var(--sky)',
  Influencer: 'var(--violet)',
  Gatekeeper: 'var(--amber)',
  'End user': 'var(--ink-4)',
};

const CORE_ROLES = ['Economic buyer', 'Champion', 'Technical DM', 'Blocker'];

function buildTree(contacts) {
  const byId = new Map(contacts.map((c) => [c.id, { ...c, children: [] }]));
  const roots = [];
  for (const c of byId.values()) {
    const parent = c.reports_to?.id && byId.has(c.reports_to.id) ? byId.get(c.reports_to.id) : null;
    if (parent) parent.children.push(c);
    else roots.push(c);
  }
  return roots;
}

function OrgNode({ node, isRoot }) {
  return (
    <div className="org-branch">
      <div className={`org-node ${isRoot ? 'root' : ''}`}>
        <div className="t-sm fw-650">{node.full_name}</div>
        <div className="t-2xs ink-3">{node.job_title ?? '—'}</div>
        {node.buying_role && (
          <div className="t-2xs mt-1" style={{ color: isRoot ? undefined : ROLE_COLOR[node.buying_role] ?? 'var(--ink-4)' }}>
            <span className="dot-ind me-1" style={{ background: ROLE_COLOR[node.buying_role] ?? 'var(--ink-4)' }} />
            {node.buying_role}
          </div>
        )}
      </div>
      {node.children?.length > 0 && (
        <>
          <div className="org-line" />
          <div className="org-row mt-0">
            {node.children.map((child) => <OrgNode key={child.id} node={child} isRoot={false} />)}
          </div>
        </>
      )}
    </div>
  );
}

export function RelationshipMapModal({ open, onClose, initialAccountId }) {
  const [accountId, setAccountId] = useState(initialAccountId ?? '');
  const options = useContactOptions();

  useEffect(() => {
    if (open) setAccountId(initialAccountId ?? '');
  }, [open, initialAccountId]);

  const { data, isLoading } = useContacts(accountId ? { account_id: accountId, limit: 100 } : undefined);
  const contacts = useMemo(() => data?.data ?? [], [data]);
  const tree = useMemo(() => buildTree(contacts), [contacts]);
  const accountName = options.accounts.find((a) => a.value === accountId)?.label;

  const relQueries = useQueries({
    queries: contacts.map((c) => ({
      queryKey: queryKeys.contacts.relationships(c.id),
      queryFn: () => contactsApi.listContactRelationships(c.id),
      enabled: open && Boolean(accountId),
    })),
  });
  const relationships = relQueries.flatMap((q, i) => (q.data?.data ?? []).map((r) => ({ ...r, from: contacts[i] })));

  const rolesPresent = new Set(contacts.map((c) => c.buying_role).filter(Boolean));
  const gaps = CORE_ROLES.filter((r) => !rolesPresent.has(r));
  const coverageScore = CORE_ROLES.length ? Math.round(((CORE_ROLES.length - gaps.length) / CORE_ROLES.length) * 100) : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Relationship Map"
      size="xl"
      footer={<button type="button" className="btn btn-ghost btn-sm-x" onClick={onClose}>Close</button>}
    >
      <div className="row g-2 align-items-end mb-4">
        <div className="col-md-5">
          <label className="lbl" htmlFor="rmAccount">Account</label>
          <select id="rmAccount" className="form-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Choose an account…</option>
            {options.accounts.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div className="col-md-7">
          <div className="d-flex flex-wrap gap-3 t-2xs ink-3">
            {Object.entries(ROLE_COLOR).slice(0, 5).map(([role, colour]) => (
              <span key={role}><span className="dot-ind me-1" style={{ background: colour }} />{role}</span>
            ))}
          </div>
        </div>
      </div>

      {!accountId ? (
        <p className="t-sm ink-3 text-center py-4">Pick an account to see who's on the buying committee.</p>
      ) : isLoading ? (
        <Spinner />
      ) : contacts.length === 0 ? (
        <p className="t-sm ink-3 text-center py-4">No contacts at this account yet.</p>
      ) : (
        <>
          <div className="d-flex flex-column align-items-center">
            <div className="org-node root mb-0" style={{ marginBottom: 0 }}>
              <div className="t-sm fw-650">{accountName ?? 'Account'}</div>
              <div className="t-2xs">{contacts.length} contact{contacts.length === 1 ? '' : 's'}</div>
            </div>
            <div className="org-line" />
            <div className="org-row">
              {tree.map((node) => <OrgNode key={node.id} node={node} isRoot={false} />)}
            </div>
          </div>

          <div className="row g-3 mt-3">
            <div className="col-lg-7">
              <div className="card-x h-100">
                <div className="card-head py-2"><h5 className="t-sm mb-0">Linked records</h5></div>
                <div className="card-body-x">
                  {relationships.length === 0 ? (
                    <p className="t-xs ink-3 mb-0">No relationships recorded between these contacts yet.</p>
                  ) : (
                    <ul className="list-x">
                      {relationships.map((r) => (
                        <li key={r.id} className="list-x-item">
                          <div className="t-sm">
                            <strong>{r.from.full_name}</strong> {r.relationship} <strong>{r.contact.full_name}</strong>
                          </div>
                          {r.strength && <span className="t-2xs ink-4 ms-2">{r.strength}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="card-x h-100">
                <div className="card-head py-2 d-flex align-items-center">
                  <h5 className="t-sm mb-0">Coverage assessment</h5>
                  <span className={`ms-auto pill ${gaps.length === 0 ? 'pill-green' : 'pill-amber'}`}>{coverageScore}%</span>
                </div>
                <div className="card-body-x">
                  {gaps.length === 0 ? (
                    <p className="t-xs ink-2 mb-0">Every core buying role has someone assigned.</p>
                  ) : (
                    <>
                      <p className="t-xs ink-3 mb-2">No one is flagged for:</p>
                      <ul className="t-xs mb-0 ps-3">
                        {gaps.map((g) => <li key={g}>{g}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
